/**
 * Список розыгрышей с постраничной загрузкой: история игрока (с его результатом) или все
 * розыгрыши площадки (для администратора). Строка открывает повтор розыгрыша.
 * На узких экранах таблица превращается в карточки.
 */
import { useState } from 'react';
import type { GameListItem, Page } from '@mt/protocol';
import { t } from '../../i18n';
import { useLoad } from '../../lib/api';
import { ACTION_TITLES, cr, crn, pts } from '../../lib/format';
import { Link } from '../../lib/router';
import { variantOfCategory, variantTitle } from '../../lib/variants';
import { useApp } from '../../net/client';
import { dateTime, Empty, Pager, toneOf } from '../kit';

/** Метка режима игры у стола; «Слепая тройка» (исходный режим) — без метки, чтобы не загромождать список. */
function VariantTag({ categoryId }: { categoryId: string }) {
  const v = variantOfCategory(useApp((s) => s.info), categoryId);
  return v && v.instrument ? <span className="tag">{variantTitle(v)}</span> : null;
}

/** Вид розыгрыша — вычисляется при отрисовке, чтобы следовать текущему языку. */
const kindTitle = (k: GameListItem['kind']): string => t(`profile.kind.${k}`);

export function GameList({ load, replayPath, mode, pageSize = 20 }: { load: (offset: number, limit: number) => Promise<Page<GameListItem>>; replayPath: (id: string) => string; mode: 'player' | 'admin'; pageSize?: number }) {
  const [offset, setOffset] = useState(0);
  const { data, error, loading } = useLoad(() => load(offset, pageSize), [offset]);
  if (error) return <p className="notice danger">{error}</p>;
  if (!data) return <p className="muted-line">{loading ? t('common.loading') : ''}</p>;
  if (data.total === 0) return <Empty>{mode === 'player' ? t('profile.games.emptyPlayer') : t('profile.games.emptyAdmin')}</Empty>;
  return (
    <>
      <div className="table-scroll">
        <table className="data-table games-table">
          <thead>
            <tr>
              <th>{t('profile.col.time')}</th>
              <th>{t('profile.col.table')}</th>
              <th>{t('profile.col.composition')}</th>
              <th className="num">Δ</th>
              {mode === 'player' ? (
                <>
                  <th>{t('profile.col.forecast')}</th>
                  <th className="num">{t('profile.col.contributed')}</th>
                  <th className="num">{t('profile.col.net')}</th>
                </>
              ) : (
                <>
                  <th className="num">{t('profile.col.players')}</th>
                  <th className="num">{t('profile.col.pot')}</th>
                  <th className="num">{t('profile.col.commission')}</th>
                </>
              )}
              <th />
            </tr>
          </thead>
          <tbody>
            {data.items.map((g) => (
              <tr key={g.gameId} className={g.status}>
                <td data-label={t('profile.col.time')} className="mono">
                  {dateTime(g.settledAt)}
                </td>
                <td data-label={t('profile.col.table')}>
                  {cr(g.ante)}
                  <VariantTag categoryId={g.categoryId} />
                  {g.mode === 'replay' && <span className="tag">{t('profile.games.historyTag')}</span>}
                </td>
                <td data-label={t('profile.col.composition')} className="mono small">
                  {g.composition.join(' · ') || '—'}
                </td>
                <td data-label="Δ" className={`num mono ${g.delta ? toneOf(g.delta) : ''}`}>
                  {g.status === 'cancelled' ? <span className="dim">{kindTitle(g.kind)}</span> : g.kind === 'walkover' ? <span className="dim">{kindTitle('walkover')}</span> : pts(g.delta, true)}
                </td>
                {mode === 'player' && g.me ? (
                  <>
                    <td data-label={t('profile.col.forecast')}>
                      {g.me.folded ? (
                        <span className="dim">{ACTION_TITLES.fold}</span>
                      ) : g.me.direction ? (
                        <span className={g.me.direction === 'up' ? 'up' : 'down'}>
                          {g.me.direction === 'up' ? '▲' : '▼'} {pts(g.me.points)}
                          {g.me.correct && g.me.error !== null && <span className="dim small"> · {t('profile.games.error', { v: pts(g.me.error) })}</span>}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td data-label={t('profile.col.contributed')} className="num mono">
                      {crn(g.me.contributed)}
                    </td>
                    <td data-label={t('profile.col.net')} className={`num mono ${toneOf(g.me.net)}`}>
                      {g.me.net > 0 ? '+' : ''}
                      {crn(g.me.net)}
                    </td>
                  </>
                ) : (
                  <>
                    <td data-label={t('profile.col.players')} className="num mono">
                      {g.humans}/{g.players}
                    </td>
                    <td data-label={t('profile.col.pot')} className="num mono">
                      {crn(g.pot)}
                    </td>
                    <td data-label={t('profile.col.commission')} className="num mono">
                      {crn(g.commission)}
                    </td>
                  </>
                )}
                <td className="num row-action">
                  <Link className="btn tiny" to={replayPath(g.gameId)}>
                    {t('profile.games.replay')}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager offset={data.offset} limit={data.limit} total={data.total} onPage={setOffset} />
    </>
  );
}
